import { useCallback, useMemo } from 'react';

import type { PerimeterXEnforcerChallenge } from '@change-corgi/core/perimeterx';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

type Result = Readonly<{
	trackShow: (operationName?: string, properties?: TrackingProperties) => void;
	trackSuccess: (
		pxResponse: PerimeterXEnforcerChallenge,
		operationName?: string,
		properties?: TrackingProperties,
	) => void;
	trackFailure: (
		pxResponse: PerimeterXEnforcerChallenge,
		operationName?: string,
		properties?: TrackingProperties,
	) => void;
}>;

/** `properties` is merged into each event, for flow-specific context such as the request's subject. */
export function usePerimeterXTracking(form: string): Result {
	const track = useTracking();

	const getProps = useCallback(
		(
			pxResponse: PerimeterXEnforcerChallenge,
			operationName?: string,
			properties?: TrackingProperties,
		): TrackingProperties => ({
			...properties,
			form,
			operation_name: operationName,
			pxRefId: pxResponse.uuid,
			pxVid: pxResponse.vid,
		}),
		[form],
	);

	return useMemo(
		() => ({
			trackShow: (operationName, properties) => {
				void track('px_captcha_shown', {
					...properties,
					form,
					operation_name: operationName,
				});
			},
			trackSuccess: (pxResponse, operationName, properties) => {
				void track('px_captcha_solved', getProps(pxResponse, operationName, properties));
			},
			trackFailure: (pxResponse, operationName, properties) => {
				void track('px_captcha_failed', getProps(pxResponse, operationName, properties));
			},
		}),
		[form, getProps, track],
	);
}
